import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  GetAvailableRegionsDocument,
  type GetAvailableRegionsQuery,
} from '../../../../../../graphql/fragment/audiences/GetAvailableRegions.generated';
import type { ExportTypeValue } from '../../../constants';
import { buildSegmentVisibilityWhere } from './utils/filterBuilder';

export type UseMarketRegionProps = {
  onRegionChange: (event: React.SyntheticEvent, code: string | null) => void;
  accountId?: string;
  isOaMatchType?: boolean;
  providerId?: string;
  exportType?: ExportTypeValue | null;
};

export type UseMarketRegionReturn = {
  regions: { code: string; name: string }[];
  regionsLoading: boolean;
  handleRegionChange: (event: React.SyntheticEvent, code: string | null) => void;
};

export const useMarketRegion = ({
  onRegionChange,
  accountId = '',
  isOaMatchType = false,
  providerId,
  exportType,
}: UseMarketRegionProps): UseMarketRegionReturn => {
  const shouldFetchRegions = Boolean(accountId);

  const segmentWhere = useMemo(
    () =>
      shouldFetchRegions
        ? buildSegmentVisibilityWhere({
            accountId,
            isOaMatchType,
            providerId,
            exportType,
          })
        : undefined,
    [shouldFetchRegions, accountId, isOaMatchType, providerId, exportType]
  );

  const { data, loading: regionsLoading } = useQuery<GetAvailableRegionsQuery>(GetAvailableRegionsDocument, {
    skip: !shouldFetchRegions,
    variables: shouldFetchRegions ? { filter: { used: true, where: segmentWhere } } : undefined,
  });

  const regions = useMemo(
    () =>
      (data?.optionsByPath ?? [])
        .filter((region): region is NonNullable<typeof region> => region !== null)
        .map(region => ({ code: region.id, name: region.name })),
    [data?.optionsByPath]
  );
  const handleRegionChange = useCallback(
    (event: React.SyntheticEvent, code: string | null) => {
      onRegionChange(event, code ?? null);
    },
    [onRegionChange]
  );

  return {
    regions,
    regionsLoading,
    handleRegionChange,
  };
};
